import { Box, Heading, SimpleGrid, Stack, Text } from '@chakra-ui/react';

import type { PricingStudioOffers } from 'types/pricing';
import { getBrandColor } from 'utils/brandColor';

const cardBorderRadius = `${Number(mondayMindbody?.customization?.cardBorderRadius) || 12}px`;
const brandColor = getBrandColor();

interface StudioOffersBlockProps {
  block: PricingStudioOffers;
}

/**
 * "Offres disponibles en studio" — full-width card with optional ribbon,
 * title, intro lines, and a grid of free-form items. Displayed even when
 * there are no items (title + intro are still shown).
 */
export function StudioOffersBlock({ block }: StudioOffersBlockProps) {
  if (!block.enabled) return null;

  const accentColor = block.accentColor || block.ribbon?.color || brandColor;
  const orderedItems = [...block.items].sort((a, b) => a.sortOrder - b.sortOrder);

  return (
    <Box
      className="studioOffersBlock"
      mt={{ base: 6, md: 10 }}
      borderRadius={cardBorderRadius}
      overflow="hidden"
      bg="#f5f5f5"
    >
      {/* ── Ribbon bar ── */}
      {block.ribbon && block.ribbon.text && (
        <Box
          className="studioOffersRibbon"
          w="full"
          px={5}
          py={2.5}
          bg={block.ribbon.color || accentColor}
          color="white"
          fontSize="sm"
          fontWeight="700"
          textTransform="uppercase"
          letterSpacing="wide"
          borderBottomRadius={cardBorderRadius}
        >
          {block.ribbon.text}
        </Box>
      )}

      {/* ── Content ── */}
      <Box px={6} pt={0} pb={6}>
        <Stack spacing={3} mb={orderedItems.length > 0 ? 6 : 0}>
          <Heading
            as="h2"
            className="studioOffersTitle"
            fontSize={{ base: '20px', md: '24px' }}
            fontWeight="900"
            textTransform="uppercase"
            lineHeight="1.15"
            color="#1a1a1a"
          >
            {block.title}
          </Heading>
          {block.introLines.length > 0 && (
            <Stack spacing={2}>
              {block.introLines.map((line, idx) => (
                <Text key={idx} fontSize={{ base: '15px', md: '17px' }} fontWeight="600" fontFamily="'Helvetica Neue LT Std', 'Helvetica Neue', Helvetica, Arial, sans-serif" textTransform="uppercase" lineHeight="1.4" letterSpacing="0.02em" color="#1a1a1a" className="studioOffersIntro">
                  {line}
                </Text>
              ))}
            </Stack>
          )}
        </Stack>

        {orderedItems.length > 0 && (
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={{ base: 4, md: 6 }} className="studioOffersGrid">
            {orderedItems.map((item) => (
              <Box
                key={item.id}
                className="studioOfferItem"
                p={5}
                borderRadius={cardBorderRadius}
                bg="white"
                borderWidth="1px"
                borderColor="gray.200"
              >
                <Stack spacing={2}>
                  <Text className="studioOfferTitle" fontWeight="700" fontSize="md" textTransform="uppercase" letterSpacing="wide" color="#1a1a1a">
                    {item.title}
                  </Text>
                  {item.price && (
                    <Text className="studioOfferPrice" fontSize="2xl" color={accentColor}>
                      {item.price}€
                      {item.count > 1 && (
                        <Text as="span" fontSize="sm" color="#4a4a4a" fontWeight="400" ml={2}>
                          ({item.count} séances)
                        </Text>
                      )}
                    </Text>
                  )}
                  {item.validity && (
                    <Text className="studioOfferValidity" fontSize="sm" color="#4a4a4a">
                      {item.validity}
                    </Text>
                  )}
                  {item.locations && (
                    <Text className="studioOfferLocations" fontSize="xs" color="#6a6a6a">
                      {item.locations}
                    </Text>
                  )}
                  {item.description && (
                    <Text className="studioOfferDescription" fontSize="xs" color="#6a6a6a">
                      {item.description}
                    </Text>
                  )}
                </Stack>
              </Box>
            ))}
          </SimpleGrid>
        )}
      </Box>
    </Box>
  );
}
